import { useCallback, useEffect, useState } from 'react'
import { Link } from 'react-router'
import { RawStatusBadge, StatusBadge, TypeTag } from '../components/Badge'
import { FreshnessBanner } from '../components/FreshnessBanner'
import { EmptyState, ErrorState, LoadingState } from '../components/states'
import {
  fetchOrders,
  type OrderSortKey,
  type OrderSummary,
  type OrderWindow,
} from '../data/orders'
import { formatAmount, formatDate, formatIdentifier } from '../lib/format'
import './OrdersScreen.css'

/** Same cap-and-page pattern as the SKU page's order-lines list. */
const PAGE_SIZE = 20
/** Don't re-query on every keystroke. */
const SEARCH_DEBOUNCE_MS = 300

interface SortOption {
  key: OrderSortKey
  ascending: boolean
  label: string
}

/** Sort by total uses orders_display.order_total, computed server-side. */
const SORT_OPTIONS: SortOption[] = [
  { key: 'order_date', ascending: false, label: 'Newest first' },
  { key: 'order_date', ascending: true, label: 'Oldest first' },
  { key: 'order_total', ascending: false, label: 'Highest total' },
  { key: 'order_total', ascending: true, label: 'Lowest total' },
]

function sortOptionValue(o: SortOption): string {
  return `${o.key}:${o.ascending ? 'asc' : 'desc'}`
}

type LoadState =
  | { status: 'loading' }
  | { status: 'error'; detail: string }
  | { status: 'ready'; orders: OrderSummary[]; totalCount: number }

function shipTo(order: { shipToCity: string | null; shipToState: string | null }): string | null {
  const parts = [order.shipToCity, order.shipToState].filter(Boolean)
  return parts.length > 0 ? parts.join(', ') : null
}

/**
 * Route `/` — the orders list. Default window: the last 12 months plus
 * every still-open order (JCP 2026-11-02); "Show all since 2024" lifts it.
 */
export function OrdersScreen() {
  const [searchInput, setSearchInput] = useState('')
  const [search, setSearch] = useState('')
  const [sort, setSort] = useState<SortOption>(SORT_OPTIONS[0]!)
  const [range, setRange] = useState<OrderWindow>('12m')
  const [limit, setLimit] = useState(PAGE_SIZE)
  const [state, setState] = useState<LoadState>({ status: 'loading' })

  useEffect(() => {
    const t = setTimeout(() => setSearch(searchInput), SEARCH_DEBOUNCE_MS)
    return () => clearTimeout(t)
  }, [searchInput])

  // A new search, sort or window starts back at page 1.
  useEffect(() => {
    setLimit(PAGE_SIZE)
  }, [search, sort, range])

  const load = useCallback(() => {
    setState({ status: 'loading' })
    void fetchOrders({ search, sortKey: sort.key, sortAscending: sort.ascending, window: range, limit }).then(
      (result) => {
        if (result.status === 'error') {
          setState({ status: 'error', detail: result.detail })
          return
        }
        setState({ status: 'ready', orders: result.orders, totalCount: result.totalCount })
      },
    )
  }, [search, sort, range, limit])

  useEffect(() => {
    load()
  }, [load])

  const showMore = useCallback(() => setLimit((n) => n + PAGE_SIZE), [])

  return (
    <>
      <FreshnessBanner />
      <h1>Orders</h1>
      <p className="esl-muted" style={{ marginTop: 'var(--esl-space-2)' }}>
        {range === '12m' ? 'Last 12 months, plus every open order.' : 'All orders since 2024.'}
      </p>

      <div className="esl-orders__controls">
        <div className="esl-field">
          <label className="esl-label" htmlFor="esl-orders-search">
            Search
          </label>
          <input
            id="esl-orders-search"
            className="esl-input"
            type="search"
            inputMode="search"
            autoComplete="off"
            placeholder="Order #, customer, or PO"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
          />
        </div>

        <div className="esl-field">
          <label className="esl-label" htmlFor="esl-orders-sort">
            Sort by
          </label>
          <select
            id="esl-orders-sort"
            className="esl-input"
            value={sortOptionValue(sort)}
            onChange={(e) => {
              const next = SORT_OPTIONS.find((o) => sortOptionValue(o) === e.target.value)
              if (next) setSort(next)
            }}
          >
            {SORT_OPTIONS.map((o) => (
              <option key={sortOptionValue(o)} value={sortOptionValue(o)}>
                {o.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      <label className="esl-orders__window">
        <input
          type="checkbox"
          checked={range === 'all'}
          onChange={(e) => setRange(e.target.checked ? 'all' : '12m')}
        />
        Show all since 2024
      </label>

      {state.status === 'loading' ? (
        <div className="esl-orders__state">
          <LoadingState label="Loading orders…" />
        </div>
      ) : null}

      {state.status === 'error' ? (
        <div className="esl-orders__state">
          <ErrorState title="We could not load your orders" detail={state.detail} onRetry={load} />
        </div>
      ) : null}

      {state.status === 'ready' && state.orders.length === 0 ? (
        <div className="esl-orders__state">
          <EmptyState
            title={search ? 'No matching orders' : 'No orders yet'}
            detail={search ? `Nothing matches "${search}".` : 'You have no orders on record.'}
          />
        </div>
      ) : null}

      {state.status === 'ready' && state.orders.length > 0 ? (
        <>
          <ul className="esl-orders__list">
            {state.orders.map((order) => (
              <li key={order.orderNo} className="esl-orders__row">
                <Link to={`/orders/${order.orderNo}`} className="esl-orders__row-link">
                  <OrderCard order={order} />
                </Link>
              </li>
            ))}
          </ul>

          {state.totalCount - state.orders.length > 0 ? (
            <button type="button" className="esl-btn esl-btn--secondary" onClick={showMore}>
              Show {Math.min(state.totalCount - state.orders.length, PAGE_SIZE)} more
            </button>
          ) : null}
        </>
      ) : null}
    </>
  )
}

/**
 * List card: order no · PO · customer · ship-to city, state · order date ·
 * total · status · Return / Shared tags · for an open order its next ESL
 * date, Date pending, or Past ESL promise. Job name only when present.
 */
function OrderCard({ order }: { order: OrderSummary }) {
  const where = shipTo(order)
  const open = order.status === 'Open'
  return (
    <>
      <div className="esl-orders__row-head">
        <span className="esl-orders__row-ids">
          <span className="esl-orders__row-no">{formatIdentifier(order.orderNo)}</span>
          {order.custPo ? (
            <span className="esl-identifier">PO {formatIdentifier(order.custPo)}</span>
          ) : null}
        </span>
        <span className="esl-badges">
          {order.hasReturn ? <TypeTag icon="return" label="Return" /> : null}
          {order.isShared ? <TypeTag icon="shared" label="Shared" /> : null}
          <RawStatusBadge status={order.status} />
        </span>
      </div>
      <p className="esl-orders__row-customer">{order.customerName ?? 'Unknown customer'}</p>
      {where || order.jobName ? (
        <p className="esl-muted">
          {[where, order.jobName ? `Job: ${order.jobName}` : null].filter(Boolean).join(' · ')}
        </p>
      ) : null}
      {open && (order.nextEslDate || order.hasDatePending || order.hasPastPromise) ? (
        <div className="esl-orders__row-dates">
          {order.hasPastPromise ? (
            <StatusBadge role="danger" icon="alert" label="Past ESL promise" />
          ) : null}
          {order.nextEslDate ? <span>ESL promise: {formatDate(order.nextEslDate)}</span> : null}
          {order.hasDatePending ? <StatusBadge role="warning" icon="pending" label="Date pending" /> : null}
        </div>
      ) : null}
      <div className="esl-orders__row-foot">
        <span className="esl-muted">{formatDate(order.orderDate)}</span>
        <span className="esl-orders__row-total">{formatAmount(order.orderTotal)}</span>
      </div>
    </>
  )
}
